import { useEffect, useRef } from 'react';

import { FamilyInputDataMap, PersonKey } from '../model';

export const useCalculatorUrlParams = ({
  familyMap,
  setFamilyMap,
  discountApplied,
  setDiscountApplied,
}: {
  familyMap: FamilyInputDataMap;
  setFamilyMap: React.Dispatch<React.SetStateAction<FamilyInputDataMap>>;
  discountApplied: boolean;
  setDiscountApplied: (value: boolean) => void;
}) => {
  const isSimpleModeFromUrl = useRef(true);
  const familyInputDataArray = Array.from(familyMap.entries());

  let inputParamsStr = familyInputDataArray
    .map(([personKey, inputData]) => {
      const outputStrArray = Object.entries(inputData).map(
        ([propertyName, value]) =>
          `${personKey}_${propertyName}=${String(value)}`
      );
      return outputStrArray.join('&');
    })
    .join('&');

  const modeParam = !isSimpleModeFromUrl.current ? 'advanced=true&' : '';
  const discountParam =
    discountApplied && isSimpleModeFromUrl.current
      ? 'discountApplied=true&'
      : '';

  inputParamsStr = modeParam + discountParam + inputParamsStr;

  const { birthYear, income, employmentStatus } = familyMap.get('MAIN') ?? {};

  const preSignupPreFillParamsArray = [];

  if (birthYear) {
    preSignupPreFillParamsArray.push(`birthYear=${birthYear}`);
  }

  if (income) {
    preSignupPreFillParamsArray.push(`income=${income}`);
  }

  if (employmentStatus) {
    preSignupPreFillParamsArray.push(`employmentStatus=${employmentStatus}`);
  }

  // Update url params whenever calculator inputs change
  useEffect(() => {
    const baseUrl = window.location.href.split('?')[0];
    const baseUrlWithoutHash = baseUrl.replace('#calculator', '');
    const newUrlWithParams = inputParamsStr
      ? `${baseUrlWithoutHash}?${inputParamsStr}#calculator`
      : undefined;
    if (global.window && typeof window !== 'undefined' && newUrlWithParams) {
      window.history.replaceState({}, '', newUrlWithParams);
    }
  }, [inputParamsStr, discountApplied]);

  // Pre-fill data from url params upon reload
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const urlParamEntries = Array.from(urlParams);

    const newFamilyMap: FamilyInputDataMap = new Map([['MAIN', {}]]);

    let discountAppliedFromUrl = false;

    urlParamEntries.forEach(([personKeyAndPropertyName, value]) => {
      if (personKeyAndPropertyName === 'advanced') {
        isSimpleModeFromUrl.current = false;
        return;
      }

      if (personKeyAndPropertyName === 'discountApplied') {
        discountAppliedFromUrl = true;

        return;
      }

      const [personKey, propertyName] = personKeyAndPropertyName.split('_');

      const currentEntry = newFamilyMap.get(personKey as PersonKey);

      let newValue;

      if (value === 'false') {
        newValue = false;
      } else if (value === 'true') {
        newValue = true;
      } else {
        newValue = value;
      }

      newFamilyMap.set(personKey as PersonKey, {
        ...currentEntry,
        [propertyName]: newValue,
      });
    });
    if (discountAppliedFromUrl) {
      setDiscountApplied(true);
    }
    setFamilyMap(newFamilyMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    calculatorParams: inputParamsStr,
    isSimpleMode: isSimpleModeFromUrl.current,
    preSignupPreFillParams:
      preSignupPreFillParamsArray.length > 0
        ? `?${preSignupPreFillParamsArray.join('&')}`
        : '',
  };
};
